import { IHttpParamSerializer, ILocationService, IScope } from 'angular';
import moment from 'moment-timezone';
import { LogService } from 'ng/src/app/logs/log.service';
import { NoteColumn, NotesTableDataRow } from 'ng/src/app/notes/notes-table/notes-table.model';
import { Note } from 'ng/src/app/notes/shared/note.model';
import { NoteService } from 'ng/src/app/notes/shared/note.service';
import { AppStateService } from 'ng/src/app/core/services/app-state.service';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { expand, finalize, takeUntil } from 'rxjs/operators';
import { ReportService } from "ng/src/app/reports/report.service";
import { LogBookUserLink } from 'ng/src/app/users/shared/user.model';
import { Utils } from 'ng/src/app/core/utils';
import { NoteActionType } from "ng/src/app/notes/note-actions/note-actions.model";
import { NoteProjections } from "app/shared/model/request-parameter.model";

angular.module('logbook').component('noteQuery', {
	template: require('./note-query.html'),
	controller: class NoteQueryController {
		static $inject = ['$scope', '$location', '$httpParamSerializer', 'noteService', 'appStateService', 'reportService'];
		constructor(
			private $scope: IScope,
			private $location: ILocationService,
			private $httpParamSerializer: IHttpParamSerializer,
			private noteService: NoteService,
			private appStateService: AppStateService,
			private reportService: ReportService
		) { }

		private defaultColumnKeys = [
			new NoteColumn('createdBy', 'Source'),
			new NoteColumn('priority', undefined, false),
			new NoteColumn('tags'),
			new NoteColumn('note'),
			new NoteColumn('acknowledgments', undefined, false),
			new NoteColumn('updatedBy', undefined, false),
			new NoteColumn('updated', 'Updated At', false),
		];

		// @Input() bindings
		exportMessage = '';
		noteList: NotesTableDataRow[] = [];
		notesMessage = 'Searching...';
		queryExecuting = false;

		getNotesSubscription?: Subscription;
		query: Note.Query = {};

		selectedLog = LogService.selectedLog!;
		users!: LogBookUserLink[];
		notesRendered = 0;
		totalNotes = 0;
		onDestroy$ = new Subject();

		resultFormat = '';
		queryFormat = '';
		validSorts = [
			{ id: 'noteTime', desc: 'Note Time' },
			{ id: 'timeframe', desc: 'Note Timeframe' },
			{ id: 'maxPriority', desc: 'Priority' },
			{ id: 'updated', desc: 'Last Updated At' },
			{ id: 'updatedBy', desc: 'Last Updated By' }
		];

		get preferencesKey() {
			return `${this.selectedLog.id}-query-notes`;
		}

		$onInit() {}

		$onDestroy() {
			this.cancelQuery();
			this.onDestroy$.next();
		}

		private getParams() {
			const { fromDate, ...params } = this.query;
			
			if (fromDate)
				params.fromTimeframe = fromDate;

			if (this.reportService.enterNotesFilterReportId)
				params.report = this.reportService.enterNotesFilterReportId;
			
			params.page = 0;

			(params.sort as string[]).forEach(sort => {
				let field = sort.split(',')[0].toLowerCase();
				field = field === 'maxpriority' ? 'priority' : field;
				const columnIx = this.defaultColumnKeys.findIndex(col => col.field.toLowerCase() === field);
				if (columnIx > -1)
					this.defaultColumnKeys[columnIx].show = true;
			});

			return params;
		}

		cancelQuery() {
			this.getNotesSubscription?.unsubscribe();
		}

		updateReport(reportId: number | null) {
			this.query.report = reportId ?? undefined;
		}

		updated = () => {
			this.getNotes();
		};

		getNotes() {
			this.cancelQuery();
			this.queryExecuting = true;

			this.notesMessage = 'Searching...';
			this.notesRendered = 0;
			this.noteList = [];

			const params = this.getParams();

			this.getNotesSubscription = this.noteService.getLogNotes(params)
				.pipe(
					expand(notes =>
						++params.page! < notes.page.totalPages ?
							this.noteService.getLogNotes(params) :
							EMPTY
					),
					finalize(() => this.queryExecuting = false)
				)
				.subscribe(
					notes => {
						const dataRows = NotesTableDataRow.fromNotes(notes, true);

						this.noteList = this.noteList.concat(dataRows);
						this.notesRendered += notes.length;
						this.totalNotes = notes.page.totalElements;
						this.notesMessage = this.notesRendered ?
							`Showing ${this.notesRendered}/${this.totalNotes} notes` :
							'No notes match the specified criteria';
						this.exportMessage = `Waiting for ${this.totalNotes - this.notesRendered} notes to finish loading...`;
					},
					error => {
						// Reset report query and reload reports if it is currently set
						if (error === 'Forbidden' || error === 403) {
							this.reportService.enterNotesFilterReportId = undefined;
							this.updated();
						}
					}
				);
		}

		onNoteTimeClicked(note: { rowIndex: number, href: string }) {
			this.$location.url('/note-detail')
				.search('note', note.href)
				.search('query', JSON.stringify(this.query))
				.search('query-format', this.queryFormat)	
				.search('result-format', this.resultFormat)
				.search('index', note.rowIndex);
		}

		onTurnoverFilterChanged(turnoverOnly: boolean) {
			if (turnoverOnly)
				this.query.turnover = true;
			else
				delete this.query.turnover;
			
			this.getNotes();
		}

		getPDFReportQueryParams = () => {
			const createdBy = this.users.find(u => u.id == this.query.createdBy),
				updatedBy = this.users.find(u => u.id == this.query.updatedBy),
				tags = this.queryFormat == 'S' ? [] :
					Array.from(document.querySelector('tag-value-selector')!
					.querySelectorAll('[data-value]'))
					.map(div => div.firstChild!.textContent!.trim());

			return {
				...this.query.fromDate && { From: Utils.formatDateString(this.query.fromDate) },
				...this.query.thruDate && { Through: Utils.formatDateString(this.query.thruDate) },
				...tags.length && { Tags: tags.join(', ') },
				...this.query.turnover && { 'Turnover Only': 'True' },
				...this.query.note && { 'Note Contains': this.query.note },
				...createdBy && { Source: `${createdBy.firstName} ${createdBy.lastName}` },
				...updatedBy && { 'Updated By': `${updatedBy.firstName} ${updatedBy.lastName}` },
				...this.query.updated && { 'Updated Since': Utils.formatDateString(this.query.updated) },
				...this.query.hasAttachments && { 'With Attachments': '' },
				Sort: [...new Set(this.query.sort)].map((s, i, array) => {
					const sort = s.split(','),
						vs = this.validSorts.find(vs => vs.id == sort[0])!,
						number = array.length > 1 ? i + 1 + '. ' : '';
	
					return `${number}${vs.desc} ${sort[1] == 'asc' ? 'Ascending' : 'Descending'}`;
				}).join('\n')
			};
		}

		downloadCSV = (showComments: boolean) => {
			const params = this.getParams();
			params.size = 500;
			params.page = 0;
			params.includeComments = showComments;

			Utils.downloadURI('/csv/notes?' 
				+ this.$httpParamSerializer(params)
				+ '&' + this.$httpParamSerializer({ 'timezone': moment.tz.guess() })
			);
		};
		
		refreshNote = (note: Note) => {
			this.noteService.getLogNote(note.id, NoteProjections.BASE)
				.pipe(
					takeUntil(this.onDestroy$)
				)
				.subscribe(note => {
					if (!note) return;
					const idx = this.noteList.findIndex(n => n.id === note.id);
					if (idx < 0) return;
					this.noteList.splice(idx, 1, ...NotesTableDataRow.fromNotes([note]));
				})
		}
		
		onNoteAction = ({note, type}: { note: Note, type: NoteActionType}) => {
			if (type === 'COMMENT')
				this.refreshNote(note);
		}
	}
});